import React, { useEffect, useRef, useContext } from "react";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";
import { ModalContext } from "../../contexts/ModalContext";

const Modal = () => {
  const context = useContext(ModalContext);

  if (!context) {
    throw new Error("Modal must be used within a ModalProvider");
  }

  const { modalState, closeModal } = context;
  const { isOpen, type, message } = modalState;
  const confirmButtonRef = useRef<HTMLButtonElement>(null);

  // ESC key handler
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        closeModal(false);
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, closeModal]);

  // Auto-focus confirm button
  useEffect(() => {
    if (isOpen && confirmButtonRef.current) {
      confirmButtonRef.current.focus();
    }
  }, [isOpen]);

  // 배경 스크롤 방지
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const isConfirm = type === "confirm";

  const handleOverlayClick = () => {
    if (!isConfirm) {
      closeModal(false);
    }
  };

  return (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContent
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <ModalIconWrapper>
          <FontAwesomeIcon
            icon={isConfirm ? faExclamationTriangle : faCheckCircle}
            size="3x"
          />
        </ModalIconWrapper>
        <ModalMessage>{message}</ModalMessage>
        <ModalButtons>
          {isConfirm && (
            <CancelButton onClick={() => closeModal(false)}>취소</CancelButton>
          )}
          <ConfirmButton
            ref={confirmButtonRef}
            onClick={() => closeModal(true)}
          >
            확인
          </ConfirmButton>
        </ModalButtons>
      </ModalContent>
    </ModalOverlay>
  );
};

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  padding: 40px;
  width: 90%;
  max-width: 500px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  text-align: center;
`;

const ModalIconWrapper = styled.div`
  color: #004094;
  margin-bottom: 20px;
`;

const ModalMessage = styled.p`
  font-size: 18px;
  color: #333;
  margin-bottom: 30px;
  line-height: 1.6;
  white-space: pre-line;
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 15px;
  justify-content: center;
`;

const ConfirmButton = styled.button`
  background: #004094;
  color: white;
  border: none;
  padding: 12px 30px;
  font-size: 16px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s;
  font-family: "Paperlogy", sans-serif;
  font-weight: 500;
  min-width: 100px;

  &:hover {
    background: #003070;
    transform: translateY(-1px);
  }

  &:focus {
    outline: 2px solid #004094;
    outline-offset: 2px;
  }
`;

const CancelButton = styled.button`
  background: #f5f5f5;
  color: #666;
  border: 1px solid #ddd;
  padding: 12px 30px;
  font-size: 16px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s;
  font-family: "Paperlogy", sans-serif;
  font-weight: 500;
  min-width: 100px;

  &:hover {
    background: #e0e0e0;
  }

  &:focus {
    outline: 2px solid #004094;
    outline-offset: 2px;
  }
`;

export default Modal;
